import { useQuery } from "@tanstack/react-query";
import {
  AccessibilityCategory,
  AccessibilityCategoryHybrid,
  AccessibilityFilters,
  BusinessNote,
  BusinessNoteAction,
  BusinessNoteTask,
  businessNotesAll,
  ComplexCategoriesData,
  VenueDetails,
} from "../../../types";
import {
  venueQueryOptions,
  venueDetailsRouteQueryOptions,
  whatsNearbyRouteQueryOptions,
} from "./venueDetailsQueryOptions";

type UseVenueDetailsBySlugProps = {
  slug: string;
  /** Optional; omitted or empty when location is not needed (e.g. business portal). */
  latitude?: string;
  longitude?: string;
  accessToken: string;
  enabled: boolean;
  type?: "Basic" | "Complex";
  syncSpaces?: boolean;
  password?: string;
  passwordToken?: string;
  ssoToken?: string;
  /**
   * Opt-in (default off). When set, a retired-slug 301 surfaces as a SlugRedirectError
   * (carrying the current canonical slug) instead of silently following the redirect —
   * lets a route redirect its URL to the canonical slug so follow-up slug-keyed calls
   * (sync-spaces / sync-accessibility) don't hit the stale slug. Widgets/native leave it off.
   */
  throwOnSlugRedirect?: boolean;
};

export type VenueDetailsBySlugResponse = {
  details: VenueDetails;
  accesibilityCategories?: AccessibilityCategory[]; // Only for Basic venues
  accesibilityCategoriesHybrid?: AccessibilityCategoryHybrid[]; // Deprecated - hybrid venues now use Enterprise data
  accesibilityCategoriesEnterprise?: ComplexCategoriesData[]; // For Complex (Hybrid + Enterprise) venues
  floorTypeOptions?: { id: number; title: string }[]; // For Complex venues
  enterpriseBusinessNotesActions?: BusinessNoteAction[]; // For Complex venues
  businessNotesTasks?: {
    Custom: BusinessNoteTask[];
    General: BusinessNoteTask[];
  }; // For Complex venues
  review_tags?: ComplexCategoriesData[]; // For Complex venues
  businessNotesAreaLocationTypes?: string[]; // For Complex venues
  businessNotesForMappers?: BusinessNote[]; // For Complex venues
  businessNotesAll?: businessNotesAll[]; // For Complex venues
  accessibility_filters?: AccessibilityFilters; // For Complex venues
  canViewPage?: number;
  passwordProtectedPageToken?: string;
  isSsoProtect?: number;
  ssoTenantId?: string;
};

/** Runs a venue-details query and normalises its result. Shared by the hooks below. */
function useVenueDetailsQuery(
  options: ReturnType<typeof venueQueryOptions>,
  enabled: boolean,
  placeholderData?: VenueDetailsBySlugResponse,
) {
  const { data, error, isLoading, isFetching, isPlaceholderData, refetch } =
    useQuery({
      ...options,
      enabled,
      placeholderData,
    });

  return {
    data,
    error,
    isLoading: isLoading || isFetching,
    isPlaceholderData,
    refetch,
  };
}

export function useVenueDetailsBySlug({
  slug,
  latitude = "",
  longitude = "",
  accessToken,
  enabled,
  type,
  syncSpaces = true,
  password,
  passwordToken,
  ssoToken,
  throwOnSlugRedirect,
}: UseVenueDetailsBySlugProps) {
  return useVenueDetailsQuery(
    venueQueryOptions({
      slug,
      latitude,
      longitude,
      accessToken,
      type,
      syncSpaces,
      password,
      passwordToken,
      ssoToken,
      throwOnSlugRedirect,
    }),
    enabled,
  );
}

/**
 * Venue-details fetch for the web venue-details route's page component.
 *
 * Takes its options from venueDetailsRouteQueryOptions — the same factory the route's
 * SSR loader uses — so the two cannot address different cache entries.
 *
 * @param props Route query params, the query's enabled flag, and optional SSR seed data.
 * @returns Venue details, error, loading state, placeholder flag and a refetch handle.
 */
export function useVenueDetailsRoute({
  slug,
  latitude = "",
  longitude = "",
  accessToken,
  enabled,
  password,
  passwordToken,
  ssoToken,
  placeholderData,
}: Omit<
  UseVenueDetailsBySlugProps,
  "throwOnSlugRedirect" | "type" | "syncSpaces"
> & {
  /**
   * SSR loader payload. The server has neither the user's token nor their coordinates,
   * so its query key can never match the client's; seeding sidesteps the dataless render
   * without hiding the client's own fetch behind a shared cache entry.
   */
  placeholderData?: VenueDetailsBySlugResponse;
}) {
  return useVenueDetailsQuery(
    venueDetailsRouteQueryOptions({
      slug,
      latitude,
      longitude,
      accessToken,
      password,
      passwordToken,
      ssoToken,
    }),
    enabled,
    placeholderData,
  );
}

/**
 * Area-map details for the web what's-nearby route's page component, from the same
 * factory its SSR loader uses.
 *
 * @param props Slug, coordinates, access token, the query's enabled flag and SSR seed data.
 * @returns Area-map details, error, loading state, placeholder flag and a refetch handle.
 */
export function useWhatsNearbyRoute({
  slug,
  latitude = "",
  longitude = "",
  accessToken,
  enabled,
  placeholderData,
}: Pick<
  UseVenueDetailsBySlugProps,
  "slug" | "latitude" | "longitude" | "accessToken" | "enabled"
> & {
  /** SSR loader payload; the server key never matches a logged-in or located client. */
  placeholderData?: VenueDetailsBySlugResponse;
}) {
  return useVenueDetailsQuery(
    whatsNearbyRouteQueryOptions({ slug, latitude, longitude, accessToken }),
    enabled,
    placeholderData,
  );
}
